import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { academicService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { Modal, Button, Input, Field } from './ui';
import { getApiErrorMessage } from '../utils/apiErrors';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** Teacher-facing sponsorship status + request button, shown in the course
 * header. Admin approval/rejection happens on the admin Sponsorships page —
 * this only ever creates a PENDING request (or shows the current one). */
export default function ModuleSponsorshipBanner({ module, onRefresh }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ start_date: todayISO(), end_date: '' });
  const [saving, setSaving] = useState(false);

  const active = module.active_sponsorship;
  const pending = module.pending_sponsorship;

  const submitRequest = async () => {
    if (!form.start_date || !form.end_date) {
      addNotification(t('Pick a start and end date'), 'error');
      return;
    }
    setSaving(true);
    try {
      await academicService.createSponsorship({
        module: module.id, start_date: form.start_date, end_date: form.end_date,
      });
      addNotification(t('Sponsorship request sent — an admin will review it.'), 'success');
      setModalOpen(false);
      onRefresh?.();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to send sponsorship request'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {active ? (
        <span className="bg-amber-400/90 text-amber-950 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1">
          ★ {t('Sponsored until {{date}}', { date: new Date(active.end_date).toLocaleDateString() })}
        </span>
      ) : pending ? (
        <span className="bg-white/20 px-3 py-1 rounded-full text-xs sm:text-sm">
          {t('Sponsorship request pending review')}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs sm:text-sm transition"
        >
          ★ {t('Request Sponsor')}
        </button>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('Request Sponsorship')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={submitRequest}>{t('Send Request')}</Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('An admin will review this request. If approved, your course is featured on the homepage and pinned at the top of the Store for the period you choose.')}
          </p>
          <Field label={t('Start Date')} required>
            <Input type="date" value={form.start_date} min={todayISO()} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </Field>
          <Field label={t('End Date')} required>
            <Input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </Field>
        </div>
      </Modal>
    </>
  );
}
